package com.swetdot.swetgram.data.moderation

import com.swetdot.swetgram.data.api.ApiClient
import com.swetdot.swetgram.protocol.dto.CreateReportRequest
import com.swetdot.swetgram.protocol.dto.ReportStatus as ReportStatusDto
import com.swetdot.swetgram.protocol.dto.ReportTargetType as ReportTargetTypeDto
import com.swetdot.swetgram.protocol.dto.ReportReason as ReportReasonDto
import io.ktor.client.request.setBody
import kotlin.time.Instant
import com.swetdot.swetgram.protocol.dto.Report as ReportDto

/**
 * `createReport` -- `POST /api/v1/reports`, 201, and the ONLY operation the contract tags
 * `Moderation`. Everything else on this surface is `Admin` and lives in `AdminRepository`.
 *
 * | operation      | verb + path         | here       |
 * |----------------|---------------------|------------|
 * | `createReport` | `POST /reports`     | [submit]   |
 *
 *
 * WHAT THE CONTRACT DECLARES, AND WHAT IT DOES NOT
 *
 * `createReport` enumerates exactly four responses: **201**, **401**, **426** and **429**. It
 * declares NO 404 and NO 400. It nevertheless answers `404 report_target_not_found` for a target
 * that does not resolve -- that divergence is the server's and is on the record in DECISIONS §19
 * item 9 and ERRORS.md -- so [submit] must handle a `NotFound` it was never promised. It does not
 * translate one: see the note on [submit].
 *
 * **429 is a declared, expected outcome, not a fault.** Reports are metered at 30 per day per
 * account (STA §23.1), and the limit is spent on EVERY attempt, including a duplicate of a report
 * the user already filed and including one whose target does not resolve. Refunding either would
 * let somebody walk uuids for free and read 201-versus-404 as an existence oracle. A caller must
 * therefore treat `ApiError.RateLimited` as an ordinary answer with its own sentence, not as a
 * transport failure.
 *
 *
 * NOTHING HERE IS RETRIED, AND THAT IS DELIBERATE
 *
 * `createReport` declares no `Idempotency-Key` header and its body carries no `client_id`, so
 * `ApiClient` is never handed a key and therefore never repeats the POST -- see its retry policy,
 * which repeats a GET and a keyed mutation and nothing else. Retry safety comes from the SERVER's
 * storage rule instead: while the reporter's earlier report about the same target is still open, a
 * repeat returns THAT report rather than filing a second one. So a POST that times out is genuinely
 * ambiguous, and the honest thing is to say the send failed and let the person decide -- a second
 * tap cannot produce a second row.
 *
 *
 * THE 201 DOES NOT SAY WHETHER ANYTHING WAS CREATED
 *
 * Because of that same rule, a 201 means "there is an open report from you about this", not "a row
 * was inserted just now". [ReportReceipt] carries the only inference that is safe to draw from it,
 * and refuses the tempting one -- see `ReportReceipt.alreadyInReview`.
 *
 *
 * AUTHENTICATION. `createReport` does not override the global `security: [bearerAuth]`, so this is
 * an ordinary authenticated call and MUST NOT pass `authenticated = false`. Checked rather than
 * assumed, because getting that backwards on an operation the contract marks `security: []`
 * deadlocks `SessionManager` for an entire access-token lifetime and then silently forever.
 */
class ReportRepository(
    private val api: ApiClient,
) : ReportSubmitter {

    /**
     * Files a report. 201.
     *
     * NOTHING IS TRANSLATED HERE. A `404` arrives as `ApiError.NotFound` and a `429` as
     * `ApiError.RateLimited`, and both travel up untouched, because the sentence a user should read
     * differs by surface -- "that post is no longer there" on a feed, "that message is gone" in a
     * conversation -- and the server has deliberately made every 404 on this path
     * indistinguishable. Deciding what a 404 MEANS is the view model's job; see
     * `ui/moderation/ReportFlow.kt`.
     *
     * [details] is trimmed by the caller and passed through as null when empty, so an untouched
     * details box does not become an empty string in a moderator's queue. The contract's 1000-
     * character cap ([REPORT_DETAILS_MAX]) is enforced in the UI rather than truncated here:
     * silently cutting a paragraph somebody took the trouble to write is worse than refusing to
     * send it.
     */
    override suspend fun submit(
        target: ReportTarget,
        reason: ReportReason,
        details: String?,
    ): ReportReceipt {
        val response: ReportDto = api.post("reports") {
            setBody(
                CreateReportRequest(
                    targetType = target.kind.toDto(),
                    targetId = target.id,
                    reason = reason.toDto(),
                    details = details,
                ),
            )
        }
        return response.toReceipt()
    }
}

/**
 * How a report reaches the server.
 *
 * AN INTERFACE, for the reason `DiscoverPostDetailSource` is one: it is what makes the report view
 * model constructible in a test on a host with no network and no database. [ReportRepository] is
 * the only implementation in the app.
 *
 * It is also the seam that keeps a Report control from ever being drawn over nothing. A null
 * submitter means reporting is not wired up on this build, and every surface hides its Report
 * action entirely rather than offering one that quietly does nothing -- which on a safety control
 * is the worse failure, because the user believes they have been heard.
 */
fun interface ReportSubmitter {
    suspend fun submit(target: ReportTarget, reason: ReportReason, details: String?): ReportReceipt
}

// ---------------------------------------------------------------------------------------------
// Boundary
// ---------------------------------------------------------------------------------------------

/**
 * Forgiving, exactly as `Mappers.kt` is: a timestamp a future server spells differently should cost
 * one line of a receipt, not the receipt.
 */
private fun parseInstantOrNull(raw: String?): Instant? =
    raw?.let { runCatching { Instant.parse(it) }.getOrNull() }

private fun ReportTargetKind.toDto(): ReportTargetTypeDto = when (this) {
    ReportTargetKind.PERSON -> ReportTargetTypeDto.USER
    ReportTargetKind.MESSAGE -> ReportTargetTypeDto.MESSAGE
    ReportTargetKind.DISCOVER_POST -> ReportTargetTypeDto.DISCOVER_POST
    ReportTargetKind.DISCOVER_COMMENT -> ReportTargetTypeDto.DISCOVER_COMMENT
    ReportTargetKind.GROUP -> ReportTargetTypeDto.GROUP
    ReportTargetKind.PICSO_POST -> ReportTargetTypeDto.PICSO_POST
}

private fun ReportReason.toDto(): ReportReasonDto = when (this) {
    ReportReason.SPAM -> ReportReasonDto.SPAM
    ReportReason.SCAM -> ReportReasonDto.SCAM
    ReportReason.HARASSMENT -> ReportReasonDto.HARASSMENT
    ReportReason.HATE_ABUSE -> ReportReasonDto.HATE_ABUSE
    ReportReason.IMPERSONATION -> ReportReasonDto.IMPERSONATION
    ReportReason.ILLEGAL_CONTENT -> ReportReasonDto.ILLEGAL_CONTENT
    ReportReason.OTHER -> ReportReasonDto.OTHER
}

/**
 * The wire enum has an `UNKNOWN` member for forward compatibility and this one keeps it, because
 * `status` is the one field on the response side the contract closes -- so an unrecognised value
 * really is a server this build does not understand, and saying "unknown" is more honest than
 * defaulting it to `open` and telling somebody their report is waiting.
 */
internal fun ReportStatusDto.toDomain(): ReportStatus = when (this) {
    ReportStatusDto.OPEN -> ReportStatus.OPEN
    ReportStatusDto.REVIEWING -> ReportStatus.REVIEWING
    ReportStatusDto.RESOLVED -> ReportStatus.RESOLVED
    ReportStatusDto.REJECTED -> ReportStatus.REJECTED
    ReportStatusDto.UNKNOWN -> ReportStatus.UNKNOWN
}

private fun ReportDto.toReceipt(): ReportReceipt = ReportReceipt(
    id = id,
    status = status.toDomain(),
    filedAt = parseInstantOrNull(createdAt),
)

/**
 * The full row, for the admin queue.
 *
 * `targetType` and `reason` stay STRINGS, because the contract leaves them bare on the response
 * side -- see `Moderation.kt`'s header. `ModerationReport.targetKind` and `.knownReason` are how a
 * caller branches, and both answer null rather than throwing.
 */
internal fun ReportDto.toDomain(): ModerationReport = ModerationReport(
    id = id,
    targetType = targetType,
    targetId = targetId,
    reason = reason,
    status = status.toDomain(),
    createdAt = parseInstantOrNull(createdAt),
    details = details,
    contentHash = contentHash,
    reporterId = reporterId,
    assignedTo = assignedTo,
    resolvedAt = parseInstantOrNull(resolvedAt),
)
